import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { PoNotificationService } from '@po-ui/ng-components';
import { CatchHandler } from '../utils/catchHandler.util';
import { TokenResponseModel } from '../models/responses/token.response.model';
import { TokenService } from './token.service';
import { GlobalService } from './global.service';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    httpHeader!: any;

    constructor(
        private httpClient: HttpClient,
        private poNotification: PoNotificationService,
        private catchHandler: CatchHandler,
        private tokenService: TokenService,
        private globalService: GlobalService
    ) {
    }

    get apiHost(): string {
        return this.globalService.getClientHost() || environment.API_URL;
    }

    private authorizationEndpoint: string = 'api/totvsmoda/internal/authorization/v2/customer-component-token';

    public async authorization(componentCode: string): Promise<string | null> {
        const request = this.httpClient.post<TokenResponseModel>(
            this.apiHost + this.authorizationEndpoint,
            {
                componentCode: componentCode
            }
        );

        return await firstValueFrom(request)
            .then((res) => {
                this.tokenService.token = res.access_token;
                return res.access_token;
            })
            .catch((err) => {
                const errors = this.catchHandler.handle(err);

                if (errors.length === 0) {
                    return null;
                }

                errors.forEach(error => {
                    this.poNotification.warning(error.message);
                });

                return null;
            });
    }

    public async setterHeader(componentCode: string) {
        const token = await this.authorization(componentCode);

        if (token) {
            this.httpHeader = {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                })
            };
        } else {
            this.poNotification.warning('Falha ao obter o token de autorização.');
        }
    }

    public postDocumentSearch(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/accounts-receivable/v2/documents/search',
            body,
            this.httpHeader
        );
    }

    public postBankSlip(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/accounts-receivable/v2/bank-slip',
            body,
            this.httpHeader
        );
    }

    public postPaymentLink(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/accounts-receivable/v2/payment-link',
            body,
            this.httpHeader
        );
    }

    public postCustomerGenerateCode(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/person/v2/customer-generate-code',
            body,
            this.httpHeader
        );
    }

    public postCustomerGeneratePassword(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/person/v2/customer-generate-password',
            body,
            this.httpHeader
        );
    }

    public postInvoicesSearch(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/fiscal/v2/invoices/search',
            body,
            this.httpHeader
        );
    }

    public getXmlContents(accessKey: string): Observable<any> {
        return this.httpClient.get<any>(
            this.apiHost + 'api/totvsmoda/internal/fiscal/v2/xml-contents/' + accessKey,
            this.httpHeader
        );
    }

    public postDanfe(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/fiscal/v2/danfe',
            body,
            this.httpHeader
        );
    }

    public postAction(action: string): Observable<any> {
        const body = {
            'actionDescription': action
        };
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/person/v2/customer-action-count',
            body,
            this.httpHeader
        );
    }

    public getWebAppData(tenant: string): Observable<any> {
        const url = `${environment.API_URL}api/totvsmoda/global/v2/webapp-data?tenant=${encodeURIComponent(tenant)}`;
        return this.httpClient.get<any>(
            url,
            this.httpHeader
        );
    }

    public postOrdersSearch(body: any): Observable<any> {
        return this.httpClient.post<any>(
            this.apiHost + 'api/totvsmoda/internal/sales-order/v2/orders/search',
            body,
            this.httpHeader
        );
    }

    public getSalesOrderItem(branchCode: number, orderCode: number): Observable<any> {
        return this.httpClient.get<any>(
            this.apiHost + 'api/totvsmoda/internal/sales-order/v2/items?branchCode=' + branchCode + '&orderCode=' + orderCode,
            this.httpHeader
        );
    }

    public getSalesOrderReport(branchCode: number, orderCode: number): Observable<any> {
        return this.httpClient.get<any>(
            this.apiHost + 'api/totvsmoda/internal/sales-order/v2/order?branchCode=' + branchCode + '&orderCode=' + orderCode,
            this.httpHeader
        );
    }
}